import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InlineSelect, InputField } from "./fields";
import { Form, Col, Row } from "react-bootstrap";
import { useFieldArray, useWatch } from "react-hook-form";
import { GrAddCircle, GrSubtractCircle } from "react-icons/gr";
import ConditionForm from "./ConditionForm";
import ActionForm from "./ActionForm";
import ScopeForm from "./ScopeForm";
import { useProgramContext } from "./ProgramContext";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { DEFAULT_ACTIONS, DEFAULT_CONDITIONS } from "./constants";
import { RuleSummary } from "./RuleSummary";
import { useAppSelector } from "../../../redux/hooks";
import { useQuery } from "@tanstack/react-query";
import { useTagsQueryOptions } from "../../../modules/advertising/api/useTags";
import { TAGGER_TO_TAG_ENTITY_TYPE } from "./taggingEntityTypes";

const RuleForm = ({ fieldName, control, isReadOnly = false }) => {
  const { t } = useTranslation(["programs"]);
  const conditionOptions = [
    { value: "all", label: t(($) => $.programs.rule.all) },
    { value: "any", label: t(($) => $.programs.rule.any) },
  ];
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { size, engine } = useProgramContext();
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const conditionsPath = `${fieldName}.conditions`;
  const actionsPath = `${fieldName}.actions`;
  const conditionLogs = useWatch({ control, name: conditionsPath }) ?? [];
  const actionLogs = useWatch({ control, name: actionsPath }) ?? [];

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({ control, name: conditionsPath });
  const {
    fields: actionFields,
    append: appendAction,
    remove: removeAction,
  } = useFieldArray({ control, name: actionsPath });

  const scope = useWatch({
    control,
    name: `${fieldName}.scope`,
  });

  const conditionLogicalOperator = useWatch({
    control,
    name: `${fieldName}.condition`,
  });

  const entityType = useWatch({ control, name: "entity_type" }) ?? "advertisement";
  const tagQueryOptions = useTagsQueryOptions({ entity_type: TAGGER_TO_TAG_ENTITY_TYPE[entityType] });
  const { data: tagsData } = useQuery({ ...tagQueryOptions, enabled: engine === "tagging" });
  const tagOptions = useMemo(() => {
    if (!tagsData?.results) return [];
    return tagsData.results.map((tag) => ({ value: tag.id, label: tag.name }));
  }, [tagsData?.results]);

  const enhancedTagQueryOptions = useTagsQueryOptions({ entity_type: "KEYWORD-TARGET" });
  const { data: enhancedTagsData } = useQuery({
    ...enhancedTagQueryOptions,
    enabled: engine === "bidding" && tagsEnhancedEnabled,
  });
  const enhancedTagOptions = useMemo(() => {
    if (!enhancedTagsData?.results) return [];
    return enhancedTagsData.results.map((tag) => ({ value: tag.id, label: tag.name }));
  }, [enhancedTagsData?.results]);

  const shortDate = useDateFormatter("short");
  const fmtDate = (date) => {
    return date ? shortDate.format(new Date(Date.parse(date))) : null;
  };
  const showEvalPeriod = !["harvesting", "tagging"].includes(engine);
  // Tagging rules support exactly one action (a single tag)
  const allowMultipleActions = engine !== "tagging";

  return (
    <Row className="mb-3 mx-3">
      <Col xs={size === "sm" || !showEvalPeriod ? 12 : 8}>
        <Row className="mb-3">
          <Col xs={12}>
            <Form.Group>
              <div className="d-flex align-items-center fs-standard">
                <strong>{t(($) => $.programs.rule.If)}</strong>
                <InlineSelect
                  options={conditionOptions}
                  fieldName={`${fieldName}.condition`}
                  control={control}
                  rules={{ required: true }}
                  disabled={isReadOnly}
                />
                <strong>{t(($) => $.programs.rule["of the following:"])}</strong>
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 11, offset: 1 }}>
            {conditionFields.map((field, index) => (
              <Row key={field.id}>
                <Col xs={12} className="d-flex align-items-center mb-2 justify-content-between pe-3">
                  <ConditionForm
                    fieldName={`${fieldName}.conditions.${index}`}
                    control={control}
                    divider={index}
                    conditionIndex={index}
                    isReadOnly={isReadOnly}
                  />
                  {!isReadOnly && (
                    <span style={{ cursor: "pointer", marginLeft: "1rem" }} onClick={() => removeCondition(index)}>
                      <GrSubtractCircle />
                    </span>
                  )}
                </Col>
              </Row>
            ))}
            {!isReadOnly && (
              <Row>
                <Col xs={12} className="d-flex justify-content-end pe-3">
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const defaults = DEFAULT_CONDITIONS[engine];
                      if (engine === "tagging" && conditionLogs?.[0]?.scope?.values?.days) {
                        appendCondition({
                          ...defaults,
                          scope: { values: { days: conditionLogs[0].scope.values.days } },
                        });
                      } else {
                        appendCondition(defaults);
                      }
                    }}
                  >
                    <GrAddCircle />
                  </span>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <Row className="my-3 ">
          <Col xs={12} className="fs-standard">
            <strong>{t(($) => $.programs.rule["Then perform the following:"])}</strong>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 11, offset: 1 }}>
            {actionFields.map((field, index) => (
              <Row key={field.id}>
                <Col xs={12} className="d-flex align-items-center mb-2 justify-content-between pe-3">
                  <ActionForm fieldName={`${fieldName}.actions.${index}`} control={control} isReadOnly={isReadOnly} />
                  {!isReadOnly && allowMultipleActions && (
                    <span style={{ cursor: "pointer", marginLeft: "1rem" }} onClick={() => removeAction(index)}>
                      <GrSubtractCircle />
                    </span>
                  )}
                </Col>
              </Row>
            ))}
            {!isReadOnly && allowMultipleActions && (
              <Row>
                <Col xs={12} className="d-flex justify-content-end pe-3">
                  <span style={{ cursor: "pointer" }} onClick={() => appendAction(DEFAULT_ACTIONS[engine])}>
                    <GrAddCircle />
                  </span>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Col>
      {showEvalPeriod && (
        <Col
          xs={size === "sm" ? 12 : 4}
          className={size === "sm" ? "mt-3" : ""}
          style={{
            padding: "2rem",
            border: "1px solid #eee",
            borderRadius: "6px",
            backgroundColor: "#f8f8f8",
          }}
        >
          <Row>
            <Col xs={12} className="fs-standard mb-3">
              <strong>{t(($) => $.programs.rule["Evaluation Period"])}</strong>
              <br />
              <small>
                {engine === "bidding" ?
                  t(($) => $.programs.rule["Specify the timing for bid adjustments"])
                : t(($) => $.programs.rule["Specify the timing for price adjustments"])}
              </small>
            </Col>
            <Col xs={12}>
              <ScopeForm fieldName={`${fieldName}.scope`} control={control} isReadOnly={isReadOnly} />
            </Col>
            <Col xs={12} className="fs-standard mt-3">
              {scope && scope.operator === "between" && (
                <span>
                  {t(($) => $.programs.rule["Check the conditions between {startDate} and {endDate}."], {
                    startDate: fmtDate(scope.values.start_date) || t(($) => $.programs.rule["start date"]),
                    endDate: fmtDate(scope.values.end_date) || t(($) => $.programs.rule["end date"]),
                  })}
                </span>
              )}
              {scope && scope.operator === "before" && (
                <span>
                  {t(($) => $.programs.rule["Check the conditions within {days} or less days since the last action."], {
                    days: scope.values.days,
                  })}
                </span>
              )}
              {scope && scope.operator === "after" && (
                <span>
                  {t(($) => $.programs.rule["Check the conditions {days} or more days after the last action."], {
                    days: scope.values.days,
                  })}
                </span>
              )}
            </Col>
          </Row>
        </Col>
      )}
      {engine.indexOf("bidding") >= 0 || engine === "tagging" ?
        <Col xs={12} className="py-3 px-0">
          <p className="info-text w-100 fs-standard mt-2 p-3">
            <RuleSummary
              conditions={conditionLogs}
              logicalOperator={conditionLogicalOperator}
              actions={actionLogs}
              marketPlace={marketPlace}
              tagOptions={tagOptions}
              enhancedTagOptions={enhancedTagOptions}
              engine={engine}
            />
          </p>
        </Col>
      : null}
      <Col xs={12} className="pt-3 px-0">
        <InputField
          label={t(($) => $.programs.harvesting.Comments)}
          fieldName={`${fieldName}.comment`}
          control={control}
          disabled={isReadOnly}
        />
      </Col>
    </Row>
  );
};

export default RuleForm;
